import "./styles/_index.scss";
import store from "../../store";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { useState, useRef, useEffect } from "react";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import WeatherLoader from "../../components/loader";

const drawerWidth = 0;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

type CriteriaDetail = {
  forecast_osf_criteria_id: number;
  field_id: number;
  field_name: string;
  margin_value: string;
  value: string;
  comparison_operator_id: number;
};
type DataRecord = Record<string, number | null | undefined>;

type WeatherSubChart = {
  dataKeys: string[];
  colors: string[];
  unit: string;
};

type WeatherSubCharts = {
  [key: string]: WeatherSubChart;
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function Overview() {
  const windowWidths = useRef(window.innerWidth);
  const [open, setOpen] = useState(windowWidths.current > 1000 ? true : false);
  const data = useSelector((state: any) => state?.app);
  const [criteriaDatas, setCriteriaDatas] = useState<any>([]);
  const [criteriaDetailDatas, setCriteriaDetailDatas] = useState<any>([]);
  const [SelectValue, setSelectValue] = useState<any>("");
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [TableDatas, setTableDatas] = useState<any>([]);
  const [activeChart, setActiveChart] = useState<string>("Wind");
  const [selectedDays, setSelectedDays] = useState<{ [key: string]: number }>({
    Wind: -1,
    Wave: -1,
    Weather: -1,
    Currents: -1,
  });

  const [activeWeatherChart, setActiveWeatherChart] =
    useState<string>("Overall");

  useEffect(() => {
    store.dispatch({
      type: "TOGGLE_MENU",
      payload: windowWidths.current > 1000 ? true : false,
    });
  }, []);

  useEffect(() => {
    setOpen(data.toggle);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_IP}/api/overview/`,
          { forecast_id: localStorage.getItem("fid") },
          {
            headers: {
              "Content-Type": "application/json; charset=UTF-8",
              Authorization: "Basic " + btoa("admin:admin"),
            },
          }
        );
        const data = response.data;
        setCriteriaDatas(data.criteria_datas || []);
        setCriteriaDetailDatas(data.criteria_detail_datas || []);
        setTableDatas(data.datas || []);
        setSelectValue(
          String(
            data.criteria_detail_datas?.[0]?.forecast_osf_criteria_id || ""
          )
        );
        setLoading(false);
      } catch (error) {
        // console.error("Error fetching table data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const hasWeatherData = (dataKeys: string[], data: any[]) => {
    return data.some((dataPoint) =>
      dataKeys.some(
        (key) =>
          dataPoint[key] !== null &&
          dataPoint[key] !== undefined &&
          !isNaN(dataPoint[key])
      )
    );
  };

  const hasValidData = (dataKeys: string[], chartData: any[]) => {
    return chartData.some((dataPoint) =>
      dataKeys.some(
        (key) =>
          dataPoint[key] !== null &&
          dataPoint[key] !== undefined &&
          !isNaN(dataPoint[key])
      )
    );
  };

  function dateFormat(date: Date) {
    var montharray = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    var dayarray = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    var day = dayarray[date.getDay()];
    var d = date.getDate();
    var m = montharray[date.getMonth()];
    return day + " " + (date.getHours() === 0 ? d - 1 : d) + " " + m;
  }

  function getColor(
    criteriaDetailDatas: CriteriaDetail[],
    data: DataRecord,
    SelectValue: string
  ): string {
    const selectedCriteria = criteriaDetailDatas.filter(
      (c_data) => c_data.forecast_osf_criteria_id === parseInt(SelectValue, 10)
    );
    if (selectedCriteria.length === 0) {
      return "white";
    }
    const colorResults = selectedCriteria.map((c_data) => {
      const fieldValue = data[c_data.field_name];
      return getColorForField(
        fieldValue,
        parseFloat(c_data.margin_value),
        parseFloat(c_data.value),
        c_data.comparison_operator_id
      );
    });
    if (colorResults.includes("red_overview")) {
      return "red_overview";
    } else if (colorResults.every((color) => color === "green_overview")) {
      return "green_overview";
    } else if (
      colorResults.includes("yellow_overview") &&
      colorResults.includes("green_overview")
    ) {
      return "yellow_overview";
    } else if (colorResults.every((color) => color === "yellow_overview")) {
      return "yellow_overview";
    }
    return "white";
  }

  function getColorForField(
    data: number | undefined | null,
    marginValue: number,
    value: number,
    comparisonOperatorId: number
  ): string {
    if (data === undefined || data === null) {
      return "white";
    }
    switch (comparisonOperatorId) {
      case 1:
        return data < value
          ? "red_overview"
          : data < marginValue
          ? "yellow_overview"
          : "green_overview";
      case 2:
        return data > value
          ? "red_overview"
          : data > marginValue
          ? "yellow_overview"
          : "green_overview";
      case 3:
        return data <= value
          ? data <= marginValue
            ? "green_overview"
            : "yellow_overview"
          : "red_overview";
      case 4:
        return data >= value
          ? data >= marginValue
            ? "green_overview"
            : "yellow_overview"
          : "red_overview";
      case 5:
        return data === value ? "green_overview" : "red_overview";
      case 6:
        return data !== value ? "green_overview" : "red_overview";
      case 7:
        return data > marginValue && data < value
          ? "green_overview"
          : "red_overview";
      case 8:
        return data === null ? "green_overview" : "red_overview";
      case 9:
        return data !== null ? "green_overview" : "red_overview";
      default:
        return "white";
    }
  }
  const hasValidDayData = (dayData: any[]) => {
    if (!dayData || dayData.length === 0) return false;

    return dayData.some((dataPoint: any) => {
      switch (activeChart) {
        case "Wind":
          return (
            (dataPoint.a_10mwindspeed !== null &&
              dataPoint.a_10mwindspeed !== undefined) ||
            (dataPoint.a_10mgust !== null &&
              dataPoint.a_10mgust !== undefined) ||
            (dataPoint.a_50mwindspeed !== null &&
              dataPoint.a_50mwindspeed !== undefined) ||
            (dataPoint.a_80mwindspeed !== null &&
              dataPoint.a_80mwindspeed !== undefined) ||
            (dataPoint.a_100mwindspeed !== null &&
              dataPoint.a_100mwindspeed !== undefined)
          );

        case "Wave":
          return (
            (dataPoint.windseaheight !== null &&
              dataPoint.windseaheight !== undefined) ||
            (dataPoint.maxwave !== null && dataPoint.maxwave !== undefined) ||
            (dataPoint.sigwaveheight !== null &&
              dataPoint.sigwaveheight !== undefined) ||
            (dataPoint.swell1height !== null &&
              dataPoint.swell1height !== undefined) ||
            (dataPoint.swell2height !== null &&
              dataPoint.swell2height !== undefined)
          );

        case "Weather":
          if (activeWeatherChart === "Overall") {
            return (
              (dataPoint.cloudbase !== null &&
                dataPoint.cloudbase !== undefined) ||
              (dataPoint.modelvisibility !== null &&
                dataPoint.modelvisibility !== undefined) ||
              (dataPoint.rainrate !== null &&
                dataPoint.rainrate !== undefined) ||
              (dataPoint.a_2mtemp !== null &&
                dataPoint.a_2mtemp !== undefined) ||
              (dataPoint.totalprecip !== null &&
                dataPoint.totalprecip !== undefined) ||
              (dataPoint.mslp !== null && dataPoint.mslp !== undefined)
            );
          } else {
            const subChart = weatherSubCharts[activeWeatherChart];
            if (subChart) {
              return subChart.dataKeys.some(
                (key) => dataPoint[key] !== null && dataPoint[key] !== undefined
              );
            }
          }
          return false;

        case "Currents":
          return (
            dataPoint.surfacecurrentspeed !== null &&
            dataPoint.surfacecurrentspeed !== undefined
          );

        default:
          return false;
      }
    });
  };
  function dateformatting(dateStr: string) {
    const parts = dateStr.split(/[\/\s:]/);
    const day = parts[0].padStart(2, "0");
    const month = parts[1].padStart(2, "0");
    const year = parts[2];
    const hours = parts.length > 3 ? parts[3] : "00";
    const minutes = parts.length > 4 ? parts[4] : "00";
    return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
  }

  function calculateWindDir(data: number): string {
    data = data % 360;
    if (data < 0) data += 360;
    const directions = [
      "N",
      "NNE",
      "NE",
      "ENE",
      "E",
      "ESE",
      "SE",
      "SSE",
      "S",
      "SSW",
      "SW",
      "WSW",
      "W",
      "WNW",
      "NW",
      "NNW",
    ];
    let closestIndex = Math.round(data / 22.5) % 16;
    return directions[closestIndex];
  }

  function formatDate1(dateString: string): string {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    return `${day}/${hours}`;
  }

  function ScrapeGraphDatas() {
    let data_list: Array<object> = [];
    const currentSelectedDay = selectedDays[activeChart];

    if (currentSelectedDay >= 0 && currentSelectedDay < TableDatas.length) {
      const selectedDayData = TableDatas[currentSelectedDay];
      selectedDayData.forEach((data: any) => {
        data["date"] = dateFormat(dateformatting(data.datetimeutc));
        data_list.push(data);
      });
    } else {
      TableDatas.slice(0, 5).forEach((datas: any) => {
        datas.forEach((data: any) => {
          data["date"] = dateFormat(dateformatting(data.datetimeutc));
          data_list.push(data);
        });
      });
    }
    return data_list;
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const formatTime = (datetimeutc: string) => {
    const date = new Date(datetimeutc);
    const hours = date.getHours().toString().padStart(2, "0");
    return `${hours}:00`;
  };

  const formatDate = (datetimeutc: string) => {
    const date = new Date(datetimeutc);
    return date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const dataKeyNames: { [key: string]: string } = {
    a_10mwindspeed: "10m WindSpeed (kn)",
    a_10mgust: "10m Gust (kn)",
    a_50mwindspeed: "50m WindSpeed (kn)",
    a_80mwindspeed: "80m WindSpeed (kn)",
    a_100mwindspeed: "100m WindSpeed (kn)",
    a_10mwinddir: "10m Wind Direction (°)",
    windseaheight: "Wind Sea Height (m)",
    windseaperiod: "Wind Sea Period (s)",
    maxwave: "Max Wave Height (m)",
    sigwaveheight: "Significant Wave Height (m)",
    swell1height: "Swell 1 Height (m)",
    swell1period: "Swell 1 Period (s)",
    swell2height: "Swell 2 Height (m)",
    swell2period: "Swell 2 Period (s)",
    windseadirection: "Wind Sea Direction (°)",
    cloudbase: "Cloud Cover (m)",
    modelvisibility: "Visibility (km)",
    rainrate: "Rain Rate (mm/hr)",
    a_2mtemp: "Temperature (°C)",
    totalprecip: "Pressure (hPa)",
    mslp: "Humidity (%)",
    surfacecurrentspeed: "Surface Current Speed (m/s)",
    surfacecurrentdirection: "Surface Current Direction (°)",
  };

  const weatherSubCharts: WeatherSubCharts = {
    Overall: {
      dataKeys: [
        "cloudbase",
        "modelvisibility",
        "rainrate",
        "a_2mtemp",
        "totalprecip",
        "mslp",
      ],
      colors: [
        "#DC143C",
        "#00CED1",
        "#7FFF00",
        "#8A2BE2",
        "#FF8C00",
        "#708090",
      ],
      unit: "mixed",
    },
    "Cloud Cover": {
      dataKeys: ["cloudbase"],
      colors: ["#DC143C"],
      unit: "m",
    },
    Visibility: {
      dataKeys: ["modelvisibility"],
      colors: ["#00CED1"],
      unit: "km",
    },
    "Rain Rate": {
      dataKeys: ["rainrate"],
      colors: ["#7FFF00"],
      unit: "mm/hr",
    },
    Temperature: {
      dataKeys: ["a_2mtemp"],
      colors: ["#8A2BE2"],
      unit: "°C",
    },
    Pressure: {
      dataKeys: ["totalprecip"],
      colors: ["#FF8C00"],
      unit: "hPa",
    },
    Humidity: {
      dataKeys: ["mslp"],
      colors: ["#708090"],
      unit: "%",
    },
  };

  return (
    <div className={open ? "sideNavOpen" : "sideNavClose"}>
      <Box className="fug-container bg-default flex sidenav-full">
        <div className="content-wrap dashboard">
          <Main
            open={open}
            className={"main"}
            style={{
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "15px",
              marginBottom: "20px",
            }}
          >
            {loading ? (
              <div
                className={"overview_table_container"}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <WeatherLoader />
              </div>
            ) : criteriaDatas.length === 0 && TableDatas.length === 0 ? (
              <h1>No Criteria Datas Available</h1>
            ) : (
              <div className={"maincontainer"}>
                <div className={"heading-container"}>
                  <span className={"heading"}>
                    5 Day Forecast Quick Summary{" "}
                  </span>
                  <Stack
                    className={"weather_window"}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      padding: "10px",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      backgroundColor: "#f9f9f9",
                      maxWidth: "100%",
                    }}
                  >
                    <span className={"heading"}>
                      Weather Window Criteria Name
                    </span>
                    <FormControl
                      sx={{ m: 1, minWidth: 180, width: "100%" }}
                      size="small"
                    >
                      <Select
                        labelId="model-data"
                        id="model-data"
                        value={
                          criteriaDatas.length === 0
                            ? "None Defined"
                            : SelectValue
                        }
                        onChange={(s) => {
                          if (criteriaDatas.length === 0) {
                            return;
                          }
                          setSelectValue(s.target.value);
                        }}
                        style={{
                          height: 30,
                          backgroundColor: "white",
                          borderRadius: "10px",
                          textAlign: "center",
                          fontSize: 15,
                        }}
                      >
                        {criteriaDatas.length > 0 ? (
                          criteriaDatas.map((data: any) => (
                            <MenuItem
                              key={data.forecast_osf_criteria_id}
                              value={data.forecast_osf_criteria_id}
                            >
                              {data.criteria_name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="None Defined" disabled>
                            None Defined
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Stack>
                </div>
                <div className={"overview_table_container"}>
                  {criteriaDatas.length > 0 &&
                  criteriaDetailDatas.length > 0 ? (
                    <Paper
                      sx={{
                        width: "90%",
                        overflow: "hidden",
                        borderRadius: "10px",
                      }}
                    >
                      <TableContainer sx={{ maxHeight: "100%" }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              {TableDatas.slice(0, 5).map(
                                (datas: any, rowIndex: number) => (
                                  <TableCell
                                    key={rowIndex}
                                    style={{
                                      padding: 0,
                                      borderRight: "1px solid #e0e0e0",
                                    }}
                                  >
                                    <div
                                      className={"mini_color_box"}
                                      style={{
                                        display: "flex",
                                        minHeight: "35px",
                                        flexDirection: "row",
                                      }}
                                    >
                                      {datas.map(
                                        (data: any, innerIndex: number) => {
                                          return (
                                            <div
                                              key={innerIndex}
                                              style={{
                                                width: "100%",
                                                position: "relative",
                                                textAlign: "center",
                                              }}
                                              className={getColor(
                                                criteriaDetailDatas,
                                                data,
                                                SelectValue
                                              )}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "11px",
                                                  color: "black",
                                                  fontWeight: "bold",
                                                  zIndex: 1,
                                                  lineHeight: "1.2",
                                                  padding: "2px",
                                                }}
                                              >
                                                {formatTime(data.datetimeutc)}
                                              </span>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  </TableCell>
                                )
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              {TableDatas.slice(0, 5).map(
                                (datas: any, rowIndex: number) => (
                                  <TableCell
                                    key={rowIndex}
                                    style={{
                                      textAlign: "center",
                                      borderRight: "1px solid #e0e0e0",
                                    }}
                                  >
                                    {formatDate(datas[0].datetimeutc)}
                                  </TableCell>
                                )
                              )}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  ) : null}

                  {TableDatas.length > 0 && (
                    <Stack
                      className={"graph"}
                      direction="column"
                      spacing={3}
                      sx={{
                        alignItems: "center",
                      }}
                    >
                      {[
                        {
                          name: "Wind",
                          dataKeys: [
                            "a_10mwindspeed",
                            "a_10mgust",
                            "a_50mwindspeed",
                            "a_80mwindspeed",
                            "a_100mwindspeed",
                          ],
                          colors: [
                            "#FF0000",
                            "#0000FF",
                            "#008000",
                            "#FFA500",
                            "#800080",
                          ],
                          directionKey: "a_10mwinddir",
                        },
                        {
                          name: "Wave",
                          dataKeys: [
                            "windseaheight",
                            "maxwave",
                            "sigwaveheight",
                            "swell1height",
                            "swell2height",
                          ],
                          colors: [
                            "#FF4500",
                            "#1E90FF",
                            "#32CD32",
                            "#FFD700",
                            "#FF69B4",
                          ],
                          directionKey: "windseadirection",
                        },
                        {
                          name: "Weather",
                          dataKeys: [
                            "cloudbase",
                            "modelvisibility",
                            "rainrate",
                            "a_2mtemp",
                            "totalprecip",
                            "mslp",
                          ],
                          colors: [
                            "#DC143C",
                            "#00CED1",
                            "#7FFF00",
                            "#8A2BE2",
                            "#FF8C00",
                            "#708090",
                          ],
                        },
                        {
                          name: "Currents",
                          dataKeys: ["surfacecurrentspeed"],
                          colors: ["#FF6347"],
                          directionKey: "surfacecurrentdirection",
                        },
                      ]
                        .filter((chart) => chart.name === activeChart)
                        .map((chart, index) => {
                          let currentDataKeys = chart.dataKeys;
                          let currentColors = chart.colors;

                          if (
                            chart.name === "Weather" &&
                            weatherSubCharts[activeWeatherChart]
                          ) {
                            currentDataKeys =
                              weatherSubCharts[activeWeatherChart].dataKeys;
                            currentColors =
                              weatherSubCharts[activeWeatherChart].colors;
                          }

                          const filteredData = ScrapeGraphDatas().map(
                            (data: any) => {
                              let newData = { ...data };
                              currentDataKeys.forEach((key) => {
                                if (
                                  newData[key] !== null &&
                                  newData[key] !== undefined
                                ) {
                                  newData[key] =
                                    Math.round(newData[key] * 10) / 10;
                                }
                              });
                              return newData;
                            }
                          );

                          const hasData = hasValidData(
                            currentDataKeys,
                            filteredData
                          );

                          if (!hasData) {
                            return null;
                          }

                          const isWeatherOverall =
                            chart.name === "Weather" &&
                            activeWeatherChart === "Overall";

                          return (
                            <Box
                              key={index}
                              sx={{
                                width: "100%",
                                padding: "20px",
                                textAlign: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  marginBottom: "5px",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                }}
                              >
                                {[
                                  {
                                    name: "Wind",
                                    dataKeys: [
                                      "a_10mwindspeed",
                                      "a_10mgust",
                                      "a_50mwindspeed",
                                      "a_80mwindspeed",
                                      "a_100mwindspeed",
                                    ],
                                  },
                                  {
                                    name: "Wave",
                                    dataKeys: [
                                      "windseaheight",
                                      "maxwave",
                                      "sigwaveheight",
                                      "swell1height",
                                      "swell2height",
                                    ],
                                  },
                                  {
                                    name: "Weather",
                                    dataKeys: [
                                      "cloudbase",
                                      "modelvisibility",
                                      "rainrate",
                                      "a_2mtemp",
                                      "totalprecip",
                                      "mslp",
                                    ],
                                  },
                                  {
                                    name: "Currents",
                                    dataKeys: ["surfacecurrentspeed"],
                                  },
                                ]
                                  .filter((chartOption) => {
                                    const filteredData = ScrapeGraphDatas();
                                    return hasValidData(
                                      chartOption.dataKeys,
                                      filteredData
                                    );
                                  })
                                  .map((chartName) => (
                                    <Button
                                      key={chartName.name}
                                      onClick={() =>
                                        setActiveChart(chartName.name)
                                      }
                                      variant={
                                        activeChart === chartName.name
                                          ? "contained"
                                          : "outlined"
                                      }
                                      sx={{
                                        minWidth: { xs: "60px", sm: "70px" },
                                        fontSize: {
                                          xs: "0.6rem",
                                          sm: "0.75rem",
                                        },
                                        padding: {
                                          xs: "2px 6px",
                                          sm: "4px 8px",
                                        },
                                        backgroundColor:
                                          activeChart === chartName.name
                                            ? "#1976d2"
                                            : "transparent",
                                        color:
                                          activeChart === chartName.name
                                            ? "white"
                                            : "#1976d2",
                                        border: "1px solid #1976d2",
                                        borderRadius: "8px",
                                        "&:hover": {
                                          backgroundColor:
                                            activeChart === chartName.name
                                              ? "#1565c0"
                                              : "#e3f2fd",
                                        },
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      {chartName.name}
                                    </Button>
                                  ))}
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginBottom: "10px",
                                }}
                              >
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                  {chart.name === "Weather"
                                    ? activeWeatherChart === "Overall"
                                      ? `${chart.name} - Overview`
                                      : `${chart.name} - ${activeWeatherChart}`
                                    : chart.name}
                                </Typography>
                              </Box>

                              {activeChart === "Weather" && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    marginBottom: "20px",
                                    flexWrap: "nowrap",
                                    gap: "10px",
                                    overflowX: "auto",
                                    paddingX: "10px",
                                  }}
                                >
                                  {Object.keys(weatherSubCharts)
                                    .filter((weatherParam) => {
                                      if (weatherParam === "Overall")
                                        return true;

                                      const subChart =
                                        weatherSubCharts[weatherParam];
                                      const currentData = ScrapeGraphDatas();

                                      return hasWeatherData(
                                        subChart.dataKeys,
                                        currentData
                                      );
                                    })
                                    .map((weatherParam) => (
                                      <Button
                                        key={weatherParam}
                                        onClick={() =>
                                          setActiveWeatherChart(weatherParam)
                                        }
                                        variant={
                                          activeWeatherChart === weatherParam
                                            ? "contained"
                                            : "outlined"
                                        }
                                        sx={{
                                          minWidth: {
                                            xs: "60px",
                                            sm: "75px",
                                            md: "85px",
                                          },
                                          maxWidth: {
                                            xs: "70px",
                                            sm: "85px",
                                            md: "95px",
                                          },
                                          fontSize: {
                                            xs: "0.45rem",
                                            sm: "0.55rem",
                                            md: "0.65rem",
                                          },
                                          padding: {
                                            xs: "3px 6px",
                                            sm: "4px 8px",
                                            md: "5px 10px",
                                          },
                                          backgroundColor:
                                            weatherParam === "Overall"
                                              ? activeWeatherChart ===
                                                weatherParam
                                                ? "#1976d2"
                                                : "transparent"
                                              : activeWeatherChart ===
                                                weatherParam
                                              ? "#2e7d32"
                                              : "transparent",
                                          color:
                                            weatherParam === "Overall"
                                              ? activeWeatherChart ===
                                                weatherParam
                                                ? "white"
                                                : "#1976d2"
                                              : activeWeatherChart ===
                                                weatherParam
                                              ? "white"
                                              : "#2e7d32",
                                          border:
                                            weatherParam === "Overall"
                                              ? "1px solid #1976d2"
                                              : "1px solid #2e7d32",
                                          borderRadius: "6px",
                                          "&:hover": {
                                            backgroundColor:
                                              weatherParam === "Overall"
                                                ? activeWeatherChart ===
                                                  weatherParam
                                                  ? "#1565c0"
                                                  : "#e3f2fd"
                                                : activeWeatherChart ===
                                                  weatherParam
                                                ? "#1b5e20"
                                                : "#e8f5e8",
                                          },
                                          transition: "all 0.3s ease",
                                          fontWeight: "600",
                                          whiteSpace: "nowrap",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                        }}
                                      >
                                        {weatherParam === "Cloud Cover"
                                          ? "Cloud Cover"
                                          : weatherParam === "Rain Rate"
                                          ? "Rain Rate"
                                          : weatherParam === "Temperature"
                                          ? "Temperature"
                                          : weatherParam}
                                      </Button>
                                    ))}
                                </Box>
                              )}
                              {selectedDays[activeChart] >= 0 && (
                                <Button
                                  onClick={() =>
                                    setSelectedDays((prev) => ({
                                      ...prev,
                                      [activeChart]: -1,
                                    }))
                                  }
                                  variant="contained"
                                  sx={{
                                    minWidth: { xs: "70px", sm: "80px" },
                                    fontSize: { xs: "0.6rem", sm: "0.75rem" },
                                    padding: { xs: "2px 6px", sm: "4px 8px" },
                                    backgroundColor: "#e53e3e",
                                    color: "white",
                                    border: "1px solid #e53e3e !important",
                                    borderRadius: "8px",
                                    "&:hover": {
                                      backgroundColor: "#c53030",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Show all dates
                                </Button>
                              )}
                              <div
                                style={{
                                  padding: "20px",
                                  backgroundColor: "#ffffff",
                                  borderRadius: "15px",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  marginBottom: "20px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    overflowX: "auto",
                                    justifyContent: "space-evenly",
                                    gap: "12px",
                                    marginBottom: "20px",
                                    padding: "10px 0",
                                  }}
                                >
                                  {TableDatas.slice(0, 5)
                                    .map(
                                      (dayData: any[], dayIndex: number) => ({
                                        dayData,
                                        dayIndex,
                                      })
                                    )
                                    .filter(({ dayData }: { dayData: any[] }) =>
                                      hasValidDayData(dayData)
                                    )
                                    .map(
                                      ({
                                        dayData,
                                        dayIndex,
                                      }: {
                                        dayData: any[];
                                        dayIndex: number;
                                      }) => {
                                        if (!dayData || dayData.length === 0)
                                          return null;

                                        const firstDataPoint = dayData[0];
                                        const currentDate = new Date(
                                          firstDataPoint.datetimeutc
                                        );
                                        const dayNumber = currentDate.getDate();
                                        const dayNames = [
                                          "Sun",
                                          "Mon",
                                          "Tue",
                                          "Wed",
                                          "Thu",
                                          "Fri",
                                          "Sat",
                                        ];
                                        const dayName =
                                          dayNames[currentDate.getDay()];

                                        const avgWindSpeed10m =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.a_10mwindspeed || 0),
                                            0
                                          ) / dayData.length;

                                        const avgWindSpeed50m =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.a_50mwindspeed || 0),
                                            0
                                          ) / dayData.length;

                                        const avgWindSpeed80m =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.a_80mwindspeed || 0),
                                            0
                                          ) / dayData.length;

                                        const avgWindSpeed100m =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.a_100mwindspeed || 0),
                                            0
                                          ) / dayData.length;

                                        const maxWindGust = Math.max(
                                          ...dayData.map(
                                            (data: any) => data.a_10mgust || 0
                                          )
                                        );

                                        const avgSigWaveHeight =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.sigwaveheight || 0),
                                            0
                                          ) / dayData.length;

                                        const maxWaveHeight = Math.max(
                                          ...dayData.map(
                                            (data: any) => data.maxwave || 0
                                          )
                                        );

                                        const avgWindSeaHeight =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.windseaheight || 0),
                                            0
                                          ) / dayData.length;

                                        const avgSwell1Height =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.swell1height || 0),
                                            0
                                          ) / dayData.length;

                                        const avgSwell2Height =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.swell2height || 0),
                                            0
                                          ) / dayData.length;

                                        const avgTemp =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.a_2mtemp || 0),
                                            0
                                          ) / dayData.length;

                                        const maxRainRate = Math.max(
                                          ...dayData.map(
                                            (data: any) => data.rainrate || 0
                                          )
                                        );

                                        const avgCloudCover =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.cloudbase || 0),
                                            0
                                          ) / dayData.length;

                                        const avgVisibility =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.modelvisibility || 0),
                                            0
                                          ) / dayData.length;

                                        const avgHumidity =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.mslp || 0),
                                            0
                                          ) / dayData.length;

                                        const avgPressure =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum + (data.totalprecip || 0),
                                            0
                                          ) / dayData.length;

                                        const avgCurrentSpeed =
                                          dayData.reduce(
                                            (sum: number, data: any) =>
                                              sum +
                                              (data.surfacecurrentspeed || 0),
                                            0
                                          ) / dayData.length;

                                        return (
                                          <div
                                            key={dayIndex}
                                            onClick={() =>
                                              setSelectedDays((prev) => ({
                                                ...prev,
                                                [activeChart]: dayIndex,
                                              }))
                                            }
                                            style={{
                                              minWidth: "120px",
                                              backgroundColor:
                                                selectedDays[activeChart] ===
                                                dayIndex
                                                  ? "#2d3748"
                                                  : "#f7fafc",
                                              color:
                                                selectedDays[activeChart] ===
                                                dayIndex
                                                  ? "white"
                                                  : "#2d3748",
                                              borderRadius: "12px",
                                              padding: "12px 8px",
                                              textAlign: "center",
                                              border:
                                                selectedDays[activeChart] ===
                                                dayIndex
                                                  ? "2px solid #3182ce"
                                                  : "1px solid #e2e8f0",
                                              position: "relative",
                                              fontSize: "14px",
                                              cursor: "pointer",
                                              transition: "all 0.3s ease",
                                              transform:
                                                selectedDays[activeChart] ===
                                                dayIndex
                                                  ? "scale(1.05)"
                                                  : "scale(1)",
                                              boxShadow:
                                                selectedDays[activeChart] ===
                                                dayIndex
                                                  ? "0 4px 12px rgba(0,0,0,0.15)"
                                                  : "none",
                                            }}
                                          >
                                            <div
                                              style={{
                                                fontSize: "20px",
                                                fontWeight: "bold",
                                                marginBottom: "2px",
                                              }}
                                            >
                                              {dayNumber}
                                            </div>

                                            <div
                                              style={{
                                                fontSize: "11px",
                                                opacity: 0.8,
                                                marginBottom: "8px",
                                              }}
                                            >
                                              {dayIndex === 0
                                                ? "Today"
                                                : `${dayName}`}
                                            </div>

                                            <div
                                              style={{
                                                fontSize: "8px",
                                                opacity: 0.8,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: "1px",
                                                maxHeight: "120px",
                                                overflowY: "auto",
                                              }}
                                            >
                                              {activeChart === "Wind" && (
                                                <>
                                                  {avgWindSpeed10m > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#FF0000",
                                                        }}
                                                      ></span>
                                                      10m:{" "}
                                                      {avgWindSpeed10m.toFixed(
                                                        1
                                                      )}
                                                      kn
                                                    </div>
                                                  )}

                                                  {maxWindGust > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#0000FF",
                                                        }}
                                                      ></span>
                                                      Gust:{" "}
                                                      {maxWindGust.toFixed(1)}
                                                      kn
                                                    </div>
                                                  )}

                                                  {avgWindSpeed50m > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#008000",
                                                        }}
                                                      ></span>
                                                      50m:{" "}
                                                      {avgWindSpeed50m.toFixed(
                                                        1
                                                      )}
                                                      kn
                                                    </div>
                                                  )}

                                                  {avgWindSpeed80m > 0 &&
                                                    avgWindSpeed80m !== 9.4 && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#FFA500",
                                                          }}
                                                        ></span>
                                                        80m:{" "}
                                                        {avgWindSpeed80m.toFixed(
                                                          1
                                                        )}
                                                        kn
                                                      </div>
                                                    )}

                                                  {avgWindSpeed100m > 0 &&
                                                    avgWindSpeed100m !==
                                                      9.4 && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#800080",
                                                          }}
                                                        ></span>
                                                        100m:{" "}
                                                        {avgWindSpeed100m.toFixed(
                                                          1
                                                        )}
                                                        kn
                                                      </div>
                                                    )}
                                                </>
                                              )}

                                              {activeChart === "Wave" && (
                                                <>
                                                  {avgWindSeaHeight > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#FF4500",
                                                        }}
                                                      ></span>
                                                      Wind Sea:{" "}
                                                      {avgWindSeaHeight.toFixed(
                                                        2
                                                      )}
                                                      m
                                                    </div>
                                                  )}

                                                  {maxWaveHeight > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#1E90FF",
                                                        }}
                                                      ></span>
                                                      Max Wave:{" "}
                                                      {maxWaveHeight.toFixed(1)}
                                                      m
                                                    </div>
                                                  )}

                                                  {avgSigWaveHeight > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#32CD32",
                                                        }}
                                                      ></span>
                                                      Sig Wave:{" "}
                                                      {avgSigWaveHeight.toFixed(
                                                        1
                                                      )}
                                                      m
                                                    </div>
                                                  )}

                                                  {avgSwell1Height > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#FFD700",
                                                        }}
                                                      ></span>
                                                      Swell1:{" "}
                                                      {avgSwell1Height.toFixed(
                                                        2
                                                      )}
                                                      m
                                                    </div>
                                                  )}

                                                  {avgSwell2Height > 0 && (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "3px",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          width: "4px",
                                                          height: "4px",
                                                          borderRadius: "50%",
                                                          backgroundColor:
                                                            "#FF69B4",
                                                        }}
                                                      ></span>
                                                      Swell2:{" "}
                                                      {avgSwell2Height.toFixed(
                                                        2
                                                      )}
                                                      m
                                                    </div>
                                                  )}
                                                </>
                                              )}

                                              {activeChart === "Weather" &&
                                                activeWeatherChart ===
                                                  "Overall" && (
                                                  <>
                                                    {hasWeatherData(
                                                      ["a_2mtemp"],
                                                      dayData
                                                    ) && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#8A2BE2",
                                                          }}
                                                        ></span>
                                                        Temp:{" "}
                                                        {avgTemp.toFixed(1)}°C
                                                      </div>
                                                    )}

                                                    {hasWeatherData(
                                                      ["rainrate"],
                                                      dayData
                                                    ) && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#7FFF00",
                                                          }}
                                                        ></span>
                                                        Rain:{" "}
                                                        {maxRainRate.toFixed(1)}
                                                        mm/h
                                                      </div>
                                                    )}

                                                    {hasWeatherData(
                                                      ["cloudbase"],
                                                      dayData
                                                    ) && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#DC143C",
                                                          }}
                                                        ></span>
                                                        Cloud:{" "}
                                                        {avgCloudCover.toFixed(
                                                          1
                                                        )}
                                                        m
                                                      </div>
                                                    )}

                                                    {hasWeatherData(
                                                      ["modelvisibility"],
                                                      dayData
                                                    ) && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#00CED1",
                                                          }}
                                                        ></span>
                                                        Vis:{" "}
                                                        {avgVisibility.toFixed(
                                                          1
                                                        )}
                                                        km
                                                      </div>
                                                    )}

                                                    {hasWeatherData(
                                                      ["mslp"],
                                                      dayData
                                                    ) && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#708090",
                                                          }}
                                                        ></span>
                                                        Humid:{" "}
                                                        {avgHumidity.toFixed(0)}
                                                        %
                                                      </div>
                                                    )}

                                                    {hasWeatherData(
                                                      ["totalprecip"],
                                                      dayData
                                                    ) && (
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                        }}
                                                      >
                                                        <span
                                                          style={{
                                                            width: "4px",
                                                            height: "4px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              "#FF8C00",
                                                          }}
                                                        ></span>
                                                        Press:{" "}
                                                        {avgPressure.toFixed(0)}
                                                        hPa
                                                      </div>
                                                    )}
                                                  </>
                                                )}

                                              {activeChart === "Weather" &&
                                                activeWeatherChart ===
                                                  "Cloud Cover" &&
                                                hasWeatherData(
                                                  ["cloudbase"],
                                                  dayData
                                                ) && (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        width: "4px",
                                                        height: "4px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          "#DC143C",
                                                      }}
                                                    ></span>
                                                    Cloud:{" "}
                                                    {avgCloudCover.toFixed(1)}
                                                    m
                                                  </div>
                                                )}

                                              {activeChart === "Weather" &&
                                                activeWeatherChart ===
                                                  "Temperature" &&
                                                hasWeatherData(
                                                  ["a_2mtemp"],
                                                  dayData
                                                ) && (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        width: "4px",
                                                        height: "4px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          "#8A2BE2",
                                                      }}
                                                    ></span>
                                                    Temp: {avgTemp.toFixed(1)}°C
                                                  </div>
                                                )}

                                              {activeChart === "Weather" &&
                                                activeWeatherChart ===
                                                  "Visibility" &&
                                                hasWeatherData(
                                                  ["modelvisibility"],
                                                  dayData
                                                ) && (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        width: "4px",
                                                        height: "4px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          "#00CED1",
                                                      }}
                                                    ></span>
                                                    Visibility:{" "}
                                                    {avgVisibility.toFixed(1)}km
                                                  </div>
                                                )}

                                              {activeChart === "Weather" &&
                                                activeWeatherChart ===
                                                  "Rain Rate" &&
                                                hasWeatherData(
                                                  ["rainrate"],
                                                  dayData
                                                ) && (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        width: "4px",
                                                        height: "4px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          "#7FFF00",
                                                      }}
                                                    ></span>
                                                    Rain:{" "}
                                                    {maxRainRate.toFixed(1)}mm/h
                                                  </div>
                                                )}

                                              {activeChart === "Weather" &&
                                                activeWeatherChart ===
                                                  "Pressure" &&
                                                hasWeatherData(
                                                  ["totalprecip"],
                                                  dayData
                                                ) && (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        width: "4px",
                                                        height: "4px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          "#FF8C00",
                                                      }}
                                                    ></span>
                                                    Pressure:{" "}
                                                    {avgPressure.toFixed(0)}hPa
                                                  </div>
                                                )}

                                              {activeChart === "Weather" &&
                                                activeWeatherChart ===
                                                  "Humidity" &&
                                                hasWeatherData(
                                                  ["mslp"],
                                                  dayData
                                                ) && (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "3px",
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        width: "4px",
                                                        height: "4px",
                                                        borderRadius: "50%",
                                                        backgroundColor:
                                                          "#708090",
                                                      }}
                                                    ></span>
                                                    Humidity:{" "}
                                                    {avgHumidity.toFixed(0)}%
                                                  </div>
                                                )}

                                              {activeChart === "Currents" && (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "3px",
                                                  }}
                                                >
                                                  <span
                                                    style={{
                                                      width: "4px",
                                                      height: "4px",
                                                      borderRadius: "50%",
                                                      backgroundColor:
                                                        "#FF6347",
                                                    }}
                                                  ></span>
                                                  Surface:{" "}
                                                  {avgCurrentSpeed.toFixed(1)}
                                                  m/s
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "15px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <h3
                                      style={{
                                        margin: 0,
                                        fontSize: "18px",
                                        fontWeight: "600",
                                        color: "#2d3748",
                                      }}
                                    >
                                      {chart.name === "Weather"
                                        ? `${chart.name} - ${activeWeatherChart}`
                                        : chart.name}
                                    </h3>
                                    {selectedDays[activeChart] >= 0 &&
                                      TableDatas[selectedDays[activeChart]] && (
                                        <span
                                          style={{
                                            fontSize: "14px",
                                            color: "#718096",
                                            marginTop: "4px",
                                          }}
                                        >
                                          {formatDate(
                                            TableDatas[
                                              selectedDays[activeChart]
                                            ][0].datetimeutc
                                          )}{" "}
                                          - Detailed View
                                        </span>
                                      )}
                                  </div>

                                  {/* <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#718096",
                                      textAlign: "right",
                                    }}
                                  >
                                    {(() => {
                                      const nextHours = filteredData.slice(
                                        0,
                                        3
                                      );
                                      const hasHighWinds = nextHours.some(
                                        (data) =>
                                          (data.a_10mwindspeed || 0) > 15
                                      );
                                      const hasHighWaves = nextHours.some(
                                        (data) =>
                                          (data.sigwaveheight || 0) > 1.0
                                      );
                                      const hasRain = nextHours.some(
                                        (data) => (data.rainrate || 0) > 0.1
                                      );
                                      const hasPoorVisibility = nextHours.some(
                                        (data) =>
                                          (data.modelvisibility || 0) < 5
                                      );
                                      const hasHighHumidity = nextHours.some(
                                        (data) => (data.mslp || 0) > 85
                                      );
                                      const hasHighPressure = nextHours.some(
                                        (data) => (data.totalprecip || 0) > 1000
                                      );
                                      const hasLowTemp = nextHours.some(
                                        (data) => (data.a_2mtemp || 0) < 5
                                      );
                                      const hasHighTemp = nextHours.some(
                                        (data) => (data.a_2mtemp || 0) > 30
                                      );

                                      if (selectedDays[activeChart] >= 0) {
                                        return `Showing detailed data for selected day (${filteredData.length} data points)`;
                                      }

                                      const getWeatherResult = () => {
                                        switch (activeChart) {
                                          case "Wind":
                                            if (hasHighWinds) {
                                              const maxWind = Math.max(
                                                ...nextHours.map(
                                                  (data) =>
                                                    data.a_10mwindspeed || 0
                                                )
                                              );
                                              return `Strong winds expected: ${maxWind.toFixed(
                                                1
                                              )}kn in next ${
                                                nextHours.length
                                              } hours.`;
                                            }
                                            return "Wind conditions stable for at least 6 hours.";

                                          case "Wave":
                                            if (hasHighWaves) {
                                              const maxWave = Math.max(
                                                ...nextHours.map(
                                                  (data) =>
                                                    data.sigwaveheight || 0
                                                )
                                              );
                                              return `High waves expected: ${maxWave.toFixed(
                                                1
                                              )}m in next ${
                                                nextHours.length
                                              } hours.`;
                                            }
                                            return "Wave conditions stable for at least 6 hours.";

                                          case "Weather":
                                            switch (activeWeatherChart) {
                                              case "Rain Rate":
                                                const avgRain =
                                                  nextHours.reduce(
                                                    (sum, data) =>
                                                      sum +
                                                      (data.rainrate || 0),
                                                    0
                                                  ) / nextHours.length;
                                                const maxRain = Math.max(
                                                  ...nextHours.map(
                                                    (data) => data.rainrate || 0
                                                  )
                                                );

                                                if (hasRain) {
                                                  return `Rain expected: ${maxRain.toFixed(
                                                    1
                                                  )}mm/hr in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else {
                                                  return `No rain expected: ${avgRain.toFixed(
                                                    1
                                                  )}mm/hr average for next ${
                                                    nextHours.length
                                                  } hours.`;
                                                }

                                              case "Visibility":
                                                const avgVis =
                                                  nextHours.reduce(
                                                    (sum, data) =>
                                                      sum +
                                                      (data.modelvisibility ||
                                                        0),
                                                    0
                                                  ) / nextHours.length;
                                                const minVis = Math.min(
                                                  ...nextHours.map(
                                                    (data) =>
                                                      data.modelvisibility || 0
                                                  )
                                                );

                                                if (hasPoorVisibility) {
                                                  return `Poor visibility expected: ${minVis.toFixed(
                                                    1
                                                  )}km in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else {
                                                  return `Visibility good: ${avgVis.toFixed(
                                                    1
                                                  )}km average for next ${
                                                    nextHours.length
                                                  } hours.`;
                                                }

                                              case "Humidity":
                                                const avgHum =
                                                  nextHours.reduce(
                                                    (sum, data) =>
                                                      sum + (data.mslp || 0),
                                                    0
                                                  ) / nextHours.length;
                                                const maxHum = Math.max(
                                                  ...nextHours.map(
                                                    (data) => data.mslp || 0
                                                  )
                                                );

                                                if (hasHighHumidity) {
                                                  return `High humidity expected: ${maxHum.toFixed(
                                                    0
                                                  )}% in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else {
                                                  return `Humidity stable: ${avgHum.toFixed(
                                                    0
                                                  )}% average for next ${
                                                    nextHours.length
                                                  } hours.`;
                                                }

                                              case "Temperature":
                                                const avgTemp =
                                                  nextHours.reduce(
                                                    (sum, data) =>
                                                      sum +
                                                      (data.a_2mtemp || 0),
                                                    0
                                                  ) / nextHours.length;
                                                const minTemp = Math.min(
                                                  ...nextHours.map(
                                                    (data) => data.a_2mtemp || 0
                                                  )
                                                );
                                                const maxTemp = Math.max(
                                                  ...nextHours.map(
                                                    (data) => data.a_2mtemp || 0
                                                  )
                                                );

                                                if (hasLowTemp) {
                                                  return `Cold conditions: ${minTemp.toFixed(
                                                    1
                                                  )}°C expected in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else if (hasHighTemp) {
                                                  return `High temperature: ${maxTemp.toFixed(
                                                    1
                                                  )}°C expected in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else {
                                                  return `Temperature stable: ${avgTemp.toFixed(
                                                    1
                                                  )}°C average for next ${
                                                    nextHours.length
                                                  } hours.`;
                                                }

                                              case "Pressure":
                                                const avgPress =
                                                  nextHours.reduce(
                                                    (sum, data) =>
                                                      sum +
                                                      (data.totalprecip || 0),
                                                    0
                                                  ) / nextHours.length;
                                                const maxPress = Math.max(
                                                  ...nextHours.map(
                                                    (data) =>
                                                      data.totalprecip || 0
                                                  )
                                                );
                                                const minPress = Math.min(
                                                  ...nextHours.map(
                                                    (data) =>
                                                      data.totalprecip || 0
                                                  )
                                                );

                                                if (hasHighPressure) {
                                                  return `High pressure: ${maxPress.toFixed(
                                                    0
                                                  )}hPa expected in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else if (minPress < 1000) {
                                                  return `Low pressure: ${minPress.toFixed(
                                                    0
                                                  )}hPa expected in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else {
                                                  return `Pressure stable: ${avgPress.toFixed(
                                                    0
                                                  )}hPa average for next ${
                                                    nextHours.length
                                                  } hours.`;
                                                }

                                              case "Cloud Cover":
                                                const avgCloudCover =
                                                  nextHours.reduce(
                                                    (sum, data) =>
                                                      sum +
                                                      (data.cloudbase || 0),
                                                    0
                                                  ) / nextHours.length;

                                                if (avgCloudCover > 6) {
                                                  return `Heavy cloud cover expected: ${avgCloudCover.toFixed(
                                                    1
                                                  )} m in next ${
                                                    nextHours.length
                                                  } hours.`;
                                                } else {
                                                  return `Cloud cover stable: ${avgCloudCover.toFixed(
                                                    1
                                                  )} m average for next ${
                                                    nextHours.length
                                                  } hours.`;
                                                }

                                              case "Overall":
                                              default:
                                                const weatherStatuses = [];

                                                if (
                                                  hasWeatherData(
                                                    ["cloudbase"],
                                                    nextHours
                                                  )
                                                ) {
                                                  const avgCloudCover =
                                                    nextHours.reduce(
                                                      (sum, data) =>
                                                        sum +
                                                        (data.cloudbase || 0),
                                                      0
                                                    ) / nextHours.length;

                                                  if (avgCloudCover > 6) {
                                                    weatherStatuses.push(
                                                      `Heavy cloud cover: ${avgCloudCover.toFixed(
                                                        1
                                                      )} m expected in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else {
                                                    weatherStatuses.push(
                                                      `Cloud cover stable: ${avgCloudCover.toFixed(
                                                        1
                                                      )} m average for next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  }
                                                }

                                                if (
                                                  hasWeatherData(
                                                    ["modelvisibility"],
                                                    nextHours
                                                  )
                                                ) {
                                                  const minVis = Math.min(
                                                    ...nextHours.map(
                                                      (data) =>
                                                        data.modelvisibility ||
                                                        0
                                                    )
                                                  );
                                                  if (minVis < 5) {
                                                    weatherStatuses.push(
                                                      `Poor visibility expected: ${minVis.toFixed(
                                                        1
                                                      )}km in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else {
                                                    const avgVis =
                                                      nextHours.reduce(
                                                        (sum, data) =>
                                                          sum +
                                                          (data.modelvisibility ||
                                                            0),
                                                        0
                                                      ) / nextHours.length;
                                                    weatherStatuses.push(
                                                      `Visibility good: ${avgVis.toFixed(
                                                        1
                                                      )}km average for next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  }
                                                }

                                                if (
                                                  hasWeatherData(
                                                    ["rainrate"],
                                                    nextHours
                                                  )
                                                ) {
                                                  const maxRain = Math.max(
                                                    ...nextHours.map(
                                                      (data) =>
                                                        data.rainrate || 0
                                                    )
                                                  );
                                                  if (maxRain > 0.1) {
                                                    weatherStatuses.push(
                                                      `Rain expected: ${maxRain.toFixed(
                                                        1
                                                      )}mm/hr in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else {
                                                    weatherStatuses.push(
                                                      `No rain expected in next ${nextHours.length} hours.`
                                                    );
                                                  }
                                                }

                                                if (
                                                  hasWeatherData(
                                                    ["a_2mtemp"],
                                                    nextHours
                                                  )
                                                ) {
                                                  const minTemp = Math.min(
                                                    ...nextHours.map(
                                                      (data) =>
                                                        data.a_2mtemp || 0
                                                    )
                                                  );
                                                  const maxTemp = Math.max(
                                                    ...nextHours.map(
                                                      (data) =>
                                                        data.a_2mtemp || 0
                                                    )
                                                  );

                                                  if (minTemp < 5) {
                                                    weatherStatuses.push(
                                                      `Cold conditions: ${minTemp.toFixed(
                                                        1
                                                      )}°C expected in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else if (maxTemp > 30) {
                                                    weatherStatuses.push(
                                                      `High temperature: ${maxTemp.toFixed(
                                                        1
                                                      )}°C expected in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else {
                                                    const avgTemp =
                                                      nextHours.reduce(
                                                        (sum, data) =>
                                                          sum +
                                                          (data.a_2mtemp || 0),
                                                        0
                                                      ) / nextHours.length;
                                                    weatherStatuses.push(
                                                      `Temperature stable: ${avgTemp.toFixed(
                                                        1
                                                      )}°C average for next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  }
                                                }

                                                if (
                                                  hasWeatherData(
                                                    ["totalprecip"],
                                                    nextHours
                                                  )
                                                ) {
                                                  const maxPress = Math.max(
                                                    ...nextHours.map(
                                                      (data) =>
                                                        data.totalprecip || 0
                                                    )
                                                  );
                                                  const minPress = Math.min(
                                                    ...nextHours.map(
                                                      (data) =>
                                                        data.totalprecip || 0
                                                    )
                                                  );

                                                  if (maxPress > 1020) {
                                                    weatherStatuses.push(
                                                      `High pressure: ${maxPress.toFixed(
                                                        0
                                                      )}hPa expected in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else if (minPress < 1000) {
                                                    weatherStatuses.push(
                                                      `Low pressure: ${minPress.toFixed(
                                                        0
                                                      )}hPa expected in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else {
                                                    const avgPress =
                                                      nextHours.reduce(
                                                        (sum, data) =>
                                                          sum +
                                                          (data.totalprecip ||
                                                            0),
                                                        0
                                                      ) / nextHours.length;
                                                    weatherStatuses.push(
                                                      `Pressure stable: ${avgPress.toFixed(
                                                        0
                                                      )}hPa average for next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  }
                                                }

                                                if (
                                                  hasWeatherData(
                                                    ["mslp"],
                                                    nextHours
                                                  )
                                                ) {
                                                  const maxHum = Math.max(
                                                    ...nextHours.map(
                                                      (data) => data.mslp || 0
                                                    )
                                                  );
                                                  if (maxHum > 85) {
                                                    weatherStatuses.push(
                                                      `High humidity expected: ${maxHum.toFixed(
                                                        0
                                                      )}% in next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  } else {
                                                    const avgHum =
                                                      nextHours.reduce(
                                                        (sum, data) =>
                                                          sum +
                                                          (data.mslp || 0),
                                                        0
                                                      ) / nextHours.length;
                                                    weatherStatuses.push(
                                                      `Humidity stable: ${avgHum.toFixed(
                                                        0
                                                      )}% average for next ${
                                                        nextHours.length
                                                      } hours.`
                                                    );
                                                  }
                                                }

                                                return weatherStatuses.length >
                                                  0
                                                  ? weatherStatuses
                                                  : [
                                                      "Weather data not available for next 6 hours.",
                                                    ];
                                            }

                                          case "Currents":
                                            const hasHighCurrents =
                                              nextHours.some(
                                                (data) =>
                                                  (data.surfacecurrentspeed ||
                                                    0) > 2.0
                                              );
                                            if (hasHighCurrents) {
                                              const maxCurrent = Math.max(
                                                ...nextHours.map(
                                                  (data) =>
                                                    data.surfacecurrentspeed ||
                                                    0
                                                )
                                              );
                                              return `Strong currents expected: ${maxCurrent.toFixed(
                                                1
                                              )}m/s in next ${
                                                nextHours.length
                                              } hours.`;
                                            }
                                            return "Current conditions stable for next 6 hours.";

                                          default:
                                            if (hasRain) {
                                              const maxRain = Math.max(
                                                ...nextHours.map(
                                                  (data) => data.rainrate || 0
                                                )
                                              );
                                              return `Rain expected: ${maxRain.toFixed(
                                                1
                                              )}mm/hr in next ${
                                                nextHours.length
                                              } hours.`;
                                            } else if (hasPoorVisibility) {
                                              const minVis = Math.min(
                                                ...nextHours.map(
                                                  (data) =>
                                                    data.modelvisibility || 0
                                                )
                                              );
                                              return `Poor visibility expected: ${minVis.toFixed(
                                                1
                                              )}km.`;
                                            } else if (hasHighHumidity) {
                                              const maxHum = Math.max(
                                                ...nextHours.map(
                                                  (data) => data.mslp || 0
                                                )
                                              );
                                              return `High humidity expected: ${maxHum.toFixed(
                                                0
                                              )}%.`;
                                            } else if (hasLowTemp) {
                                              const minTemp = Math.min(
                                                ...nextHours.map(
                                                  (data) => data.a_2mtemp || 0
                                                )
                                              );
                                              return `Cold conditions: ${minTemp.toFixed(
                                                1
                                              )}°C expected.`;
                                            } else if (hasHighTemp) {
                                              const maxTemp = Math.max(
                                                ...nextHours.map(
                                                  (data) => data.a_2mtemp || 0
                                                )
                                              );
                                              return `High temperature: ${maxTemp.toFixed(
                                                1
                                              )}°C expected.`;
                                            } else if (
                                              hasHighWinds &&
                                              hasHighWaves
                                            ) {
                                              return "Strong winds and high waves expected.";
                                            } else if (hasHighWinds) {
                                              return "Strong winds expected.";
                                            } else if (hasHighWaves) {
                                              return "High waves expected.";
                                            }
                                            return "Conditions stable for at least 6 hours.";
                                        }
                                      };

                                      const result = getWeatherResult();

                                      if (Array.isArray(result)) {
                                        return result.map((line, index) => (
                                          <div
                                            key={index}
                                            style={{
                                              marginBottom:
                                                index < result.length - 1
                                                  ? "4px"
                                                  : "0",
                                            }}
                                          >
                                            {line}
                                          </div>
                                        ));
                                      } else {
                                        return result;
                                      }
                                    })()}
                                  </div> */}
                                </div>
                                <div
                                  style={{
                                    border: "5px solid #efefef",
                                    padding: "10px",
                                  }}
                                >
                                  {chart.directionKey &&
                                    (chart.name === "Wind" ||
                                      chart.name === "Currents") && (
                                      <div
                                        style={{
                                          position: "relative",
                                          height: "60px",
                                          backgroundColor: "transparent",
                                          marginBottom: "5px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "35px",
                                            left:
                                              window.innerWidth < 1800
                                                ? "5px"
                                                : "0px",
                                            fontSize:
                                              window.innerWidth < 1800
                                                ? "8px"
                                                : "9px",
                                            fontWeight: "bold",
                                            color: "black",
                                            zIndex: 2,
                                          }}
                                        >
                                          Direction
                                        </div>

                                        <div
                                          style={{
                                            position: "absolute",
                                            left: "60px",
                                            right: "37px",
                                            top: "10px",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            paddingLeft: "15px",
                                            paddingRight: "35px",
                                            paddingTop: "20px",
                                          }}
                                        >
                                          {filteredData.map(
                                            (data: any, dataIndex: number) => {
                                              const directionValue =
                                                data[chart.directionKey];
                                              const directionText =
                                                directionValue !== null &&
                                                directionValue !== undefined
                                                  ? calculateWindDir(
                                                      directionValue
                                                    )
                                                  : "";

                                              const totalDataPoints =
                                                filteredData.length;
                                              let leftPosition;

                                              if (totalDataPoints === 1) {
                                                leftPosition = 50;
                                              } else {
                                                leftPosition =
                                                  (dataIndex /
                                                    (totalDataPoints - 1)) *
                                                  100;
                                              }

                                              return (
                                                <div
                                                  key={dataIndex}
                                                  style={{
                                                    position: "absolute",
                                                    left: `${leftPosition}%`,
                                                    transform:
                                                      "translateX(-50%)",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                  }}
                                                >
                                                  {directionValue !== null &&
                                                    directionValue !==
                                                      undefined && (
                                                      <span
                                                        style={{
                                                          fontSize:
                                                            window.innerWidth <
                                                            1800
                                                              ? "7px"
                                                              : "9px",
                                                          fontWeight: "600",
                                                          color: "#333",
                                                          fontFamily: "inherit",
                                                          textShadow:
                                                            "0 0 2px rgba(255,255,255,0.8)",
                                                        }}
                                                      >
                                                        {directionText}
                                                      </span>
                                                    )}

                                                  {directionValue !== null &&
                                                    directionValue !==
                                                      undefined && (
                                                      <div
                                                        style={{
                                                          width:
                                                            window.innerWidth <
                                                            1800
                                                              ? "7px"
                                                              : "10px",
                                                          height:
                                                            window.innerWidth <
                                                            1800
                                                              ? "25px"
                                                              : "25px",
                                                          position: "relative",
                                                          transform: `rotate(${
                                                            chart.name ===
                                                            "Wind"
                                                              ? directionValue +
                                                                180
                                                              : directionValue
                                                          }deg)`,
                                                          transition:
                                                            "transform 0.3s ease",
                                                        }}
                                                      >
                                                        <svg
                                                          version="1.1"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          viewBox="0 0 32 32"
                                                          width="100%"
                                                          height="100%"
                                                          fill="#000000"
                                                        >
                                                          <g>
                                                            <path
                                                              d="M29.9,28.6l-13-26c-0.3-0.7-1.4-0.7-1.8,0l-13,26c-0.2,0.4-0.1,0.8,0.2,1.1C2.5,30,3,30.1,3.4,29.9L16,25.1l12.6,4.9 c0.1,0,0.2,0.1,0.4,0.1c0.3,0,0.5-0.1,0.7-0.3C30,29.4,30.1,28.9,29.9,28.6z"
                                                              fill="#000000"
                                                            />
                                                          </g>
                                                        </svg>
                                                      </div>
                                                    )}
                                                </div>
                                              );
                                            }
                                          )}
                                        </div>
                                      </div>
                                    )}

                                  {isWeatherOverall ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "30px",
                                      }}
                                    >
                                      {Object.keys(weatherSubCharts)
                                        .filter((key) => key !== "Overall")
                                        .filter((weatherParam) => {
                                          const subChart =
                                            weatherSubCharts[weatherParam];
                                          const currentData = filteredData;
                                          return hasWeatherData(
                                            subChart.dataKeys,
                                            currentData
                                          );
                                        })
                                        .map((weatherParam, chartIndex) => {
                                          const subChart =
                                            weatherSubCharts[weatherParam];

                                          const subFilteredData =
                                            filteredData.map((data: any) => {
                                              let newData = { ...data };
                                              subChart.dataKeys.forEach(
                                                (key) => {
                                                  if (
                                                    newData[key] !== null &&
                                                    newData[key] !== undefined
                                                  ) {
                                                    newData[key] =
                                                      Math.round(
                                                        newData[key] * 10
                                                      ) / 10;
                                                  }
                                                }
                                              );
                                              return newData;
                                            });

                                          const hasSubData = hasValidData(
                                            subChart.dataKeys,
                                            subFilteredData
                                          );

                                          if (!hasSubData) {
                                            return null;
                                          }

                                          return (
                                            <div
                                              key={weatherParam}
                                              style={{
                                                backgroundColor: "#fffff",
                                                borderRadius: "12px",
                                                padding: "20px",
                                                boxShadow:
                                                  "0 2px 8px rgba(0,0,0,0.1)",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  textAlign: "center",
                                                  marginBottom: "15px",
                                                  fontSize: "16px",
                                                  fontWeight: "600",
                                                  color: "#2d3748",
                                                }}
                                              >
                                                {weatherParam}
                                              </div>
                                              <div
                                                style={{
                                                  position: "relative",
                                                  height:
                                                    window.innerWidth < 1600
                                                      ? "200px"
                                                      : window.innerWidth < 1700
                                                      ? "230px"
                                                      : window.innerWidth < 2500
                                                      ? "300px"
                                                      : "400px",
                                                  backgroundColor: "#f8fafc",
                                                  borderRadius: "8px",
                                                  overflow: "hidden",
                                                }}
                                              >
                                                <ResponsiveContainer
                                                  width="100%"
                                                  aspect={
                                                    open
                                                      ? windowWidth < 1000
                                                        ? 4 / 1
                                                        : windowWidth < 1900
                                                        ? 4 / 1
                                                        : windowWidth < 2000
                                                        ? 5 / 1
                                                        : 6 / 1
                                                      : windowWidth < 1000
                                                      ? 5 / 1
                                                      : windowWidth < 1900
                                                      ? 5 / 1
                                                      : windowWidth < 2000
                                                      ? 7 / 1
                                                      : 7 / 1
                                                  }
                                                >
                                                  <AreaChart
                                                    data={subFilteredData}
                                                    margin={{
                                                      top: 15,
                                                      right: 20,
                                                      left: -15,
                                                    }}
                                                  >
                                                    <CartesianGrid
                                                      strokeDasharray="3 3"
                                                      vertical={true}
                                                      stroke="#CCCCCC"
                                                      strokeOpacity={0.6}
                                                    />
                                                    <XAxis
                                                      dataKey="datetimeutc"
                                                      axisLine={false}
                                                      tick={{
                                                        fontSize:
                                                          window.innerWidth <
                                                          1800
                                                            ? 6
                                                            : 8,
                                                      }}
                                                      tickLine={false}
                                                      interval={0}
                                                      padding={{
                                                        left: 15,
                                                        right: 15,
                                                      }}
                                                      tickFormatter={(
                                                        value,
                                                        index
                                                      ) =>
                                                        index % 1 === 0
                                                          ? formatDate1(value)
                                                          : ""
                                                      }
                                                      label={{
                                                        value: "Date/Time",
                                                        position:
                                                          "insideBottomLeft",
                                                        offset:
                                                          window.innerWidth >
                                                          1800
                                                            ? 15
                                                            : 18,
                                                        style: {
                                                          textAnchor: "start",
                                                          fontSize:
                                                            window.innerWidth >
                                                            2000
                                                              ? "8px"
                                                              : "8px",
                                                          fill: "#666",
                                                          fontWeight: "bold",
                                                          transform:
                                                            "translateX(-60px)",
                                                        },
                                                      }}
                                                    />
                                                    <YAxis
                                                      yAxisId="left"
                                                      tick={{
                                                        fontSize:
                                                          window.innerWidth <
                                                          1800
                                                            ? 10
                                                            : 12,
                                                      }}
                                                      label={{
                                                        value: (() => {
                                                          const subChart =
                                                            weatherSubCharts[
                                                              weatherParam
                                                            ];
                                                          switch (
                                                            weatherParam
                                                          ) {
                                                            case "Cloud Cover":
                                                              return "Cloud (m)";
                                                            case "Visibility":
                                                              return "Visibility (km)";
                                                            case "Rain Rate":
                                                              return "Rain (mm/hr)";
                                                            case "Temperature":
                                                              return "Temperature (°C)";
                                                            case "Pressure":
                                                              return "Pressure (hPa)";
                                                            case "Humidity":
                                                              return "Humidity (%)";
                                                            default:
                                                              return (
                                                                subChart?.unit ||
                                                                ""
                                                              );
                                                          }
                                                        })(),
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        offset:
                                                          window.innerWidth >
                                                          1800
                                                            ? 22
                                                            : 20,
                                                        style: {
                                                          textAnchor: "middle",
                                                          fontSize:
                                                            window.innerWidth >
                                                            2000
                                                              ? "13px"
                                                              : "9px",
                                                          fontWeight: "bold",
                                                          fill: "#666",
                                                        },
                                                      }}
                                                    />
                                                    <Tooltip
                                                      contentStyle={{
                                                        borderRadius: "8px",
                                                        zIndex: "1",
                                                        fontSize: "10.5px",
                                                        position: "relative",
                                                        backgroundColor:
                                                          "white",
                                                        border:
                                                          "1px solid #ccc",
                                                        boxShadow:
                                                          "0 4px 8px rgba(0,0,0,0.1)",
                                                      }}
                                                      formatter={(
                                                        value,
                                                        name,
                                                        props
                                                      ) => [
                                                        `${value} ${subChart.unit}`,
                                                        name,
                                                      ]}
                                                      labelFormatter={(
                                                        label
                                                      ) => {
                                                        const date = new Date(
                                                          label
                                                        );
                                                        const day = date
                                                          .getDate()
                                                          .toString()
                                                          .padStart(2, "0");
                                                        const month = (
                                                          date.getMonth() + 1
                                                        )
                                                          .toString()
                                                          .padStart(2, "0");
                                                        const year =
                                                          date.getFullYear();
                                                        const hours = date
                                                          .getHours()
                                                          .toString()
                                                          .padStart(2, "0");
                                                        return `${day}/${month}/${year} ${hours}:00`;
                                                      }}
                                                    />
                                                    {subChart.dataKeys.map(
                                                      (dataKey, i) =>
                                                        subFilteredData.some(
                                                          (data) =>
                                                            data[dataKey] !=
                                                            null
                                                        ) ? (
                                                          <Area
                                                            key={dataKey}
                                                            type="monotone"
                                                            name={
                                                              dataKeyNames[
                                                                dataKey
                                                              ] || dataKey
                                                            }
                                                            dataKey={dataKey}
                                                            stroke={
                                                              subChart.colors[i]
                                                            }
                                                            fillOpacity={0}
                                                            fill="none"
                                                            yAxisId="left"
                                                          />
                                                        ) : null
                                                    )}
                                                  </AreaChart>
                                                </ResponsiveContainer>
                                              </div>

                                              <div
                                                style={{
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  gap: "15px",
                                                  marginTop: "10px",
                                                  flexWrap: "nowrap",
                                                }}
                                              >
                                                {subChart.dataKeys.map(
                                                  (dataKey, i) =>
                                                    subFilteredData.some(
                                                      (data) =>
                                                        data[dataKey] != null
                                                    ) ? (
                                                      <div
                                                        key={dataKey}
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: "6px",
                                                          fontSize: "10px",
                                                          color: "#4a5568",
                                                        }}
                                                      >
                                                        <div
                                                          style={{
                                                            width: "12px",
                                                            height: "12px",
                                                            borderRadius: "50%",
                                                            backgroundColor:
                                                              subChart.colors[
                                                                i
                                                              ],
                                                          }}
                                                        ></div>
                                                        {dataKeyNames[
                                                          dataKey
                                                        ] || dataKey}
                                                      </div>
                                                    ) : null
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  ) : (
                                    <>
                                      <div
                                        style={{
                                          position: "relative",
                                          height:
                                            window.innerWidth < 1600
                                              ? "200px"
                                              : window.innerWidth < 1700
                                              ? "230px"
                                              : window.innerWidth < 2500
                                              ? "300px"
                                              : "400px",
                                          backgroundColor: "#f8fafc",
                                          borderRadius: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <ResponsiveContainer
                                          width="100%"
                                          aspect={
                                            open
                                              ? windowWidth < 1000
                                                ? 4 / 1
                                                : windowWidth < 1900
                                                ? 4 / 1
                                                : windowWidth < 2000
                                                ? 5 / 1
                                                : 6 / 1
                                              : windowWidth < 1000
                                              ? 5 / 1
                                              : windowWidth < 1900
                                              ? 5 / 1
                                              : windowWidth < 2000
                                              ? 8 / 1
                                              : 7 / 1
                                          }
                                        >
                                          <AreaChart
                                            data={filteredData}
                                            margin={{
                                              top: 15,
                                              right: 20,
                                              left: -15,
                                            }}
                                          >
                                            <CartesianGrid
                                              strokeDasharray="3 3"
                                              vertical={true}
                                              stroke="#CCCCCC"
                                              strokeOpacity={0.6}
                                            />
                                            <XAxis
                                              dataKey="datetimeutc"
                                              axisLine={false}
                                              tick={{
                                                fontSize:
                                                  window.innerWidth < 1800
                                                    ? 5.5
                                                    : 10,
                                              }}
                                              tickLine={false}
                                              interval={0}
                                              padding={{ left: 15, right: 15 }}
                                              tickFormatter={(value, index) =>
                                                index % 1 === 0
                                                  ? formatDate1(value)
                                                  : ""
                                              }
                                              label={{
                                                value: "Date/Time",
                                                position: "insideBottomLeft",
                                                offset:
                                                  window.innerWidth > 1800
                                                    ? 15
                                                    : 18,
                                                style: {
                                                  textAnchor: "start",
                                                  fontSize:
                                                    window.innerWidth > 2000
                                                      ? "8px"
                                                      : "8px",
                                                  fill: "#666",
                                                  fontWeight: "bold",
                                                  transform:
                                                    "translateX(-60px)",
                                                },
                                              }}
                                            />
                                            <YAxis
                                              yAxisId="left"
                                              tick={{
                                                fontSize:
                                                  window.innerWidth < 1800
                                                    ? 10
                                                    : 14,
                                              }}
                                              label={{
                                                value: (() => {
                                                  switch (chart.name) {
                                                    case "Wind":
                                                      return "Speed (kn)";
                                                    case "Wave":
                                                      return "Height (m)";
                                                    case "Weather":
                                                      switch (
                                                        activeWeatherChart
                                                      ) {
                                                        case "Overall":
                                                          return "Weather (Mixed)";
                                                        case "Cloud Cover":
                                                          return "Cloud (m)";
                                                        case "Visibility":
                                                          return "Visibility (km)";
                                                        case "Rain Rate":
                                                          return "Rain (mm/hr)";
                                                        case "Temperature":
                                                          return "Temperature (°C)";
                                                        case "Pressure":
                                                          return "Pressure (hPa)";
                                                        case "Humidity":
                                                          return "Humidity (%)";
                                                        default:
                                                          return "Weather";
                                                      }
                                                    case "Currents":
                                                      return "Speed (m/s)";
                                                    default:
                                                      return "";
                                                  }
                                                })(),
                                                angle: -90,
                                                position: "insideLeft",
                                                offset:
                                                  window.innerWidth > 1800
                                                    ? 22
                                                    : 20,
                                                style: {
                                                  textAnchor: "middle",
                                                  fontSize:
                                                    window.innerWidth > 2000
                                                      ? "13px"
                                                      : "9px",
                                                  fontWeight: "bold",
                                                  fill: "#666",
                                                },
                                              }}
                                            />
                                            <Tooltip
                                              contentStyle={{
                                                borderRadius: "8px",
                                                zIndex: "1",
                                                fontSize: "10.5px",
                                                position: "relative",
                                                backgroundColor: "white",
                                                border: "1px solid #ccc",
                                                boxShadow:
                                                  "0 4px 8px rgba(0,0,0,0.1)",
                                              }}
                                              formatter={(
                                                value,
                                                name,
                                                props
                                              ) => [
                                                `${value} ${
                                                  chart.name === "Weather" &&
                                                  weatherSubCharts[
                                                    activeWeatherChart
                                                  ] &&
                                                  activeWeatherChart !==
                                                    "Overall"
                                                    ? weatherSubCharts[
                                                        activeWeatherChart
                                                      ].unit
                                                    : ""
                                                }`,
                                                name,
                                              ]}
                                              labelFormatter={(label) => {
                                                const date = new Date(label);
                                                const day = date
                                                  .getDate()
                                                  .toString()
                                                  .padStart(2, "0");
                                                const month = (
                                                  date.getMonth() + 1
                                                )
                                                  .toString()
                                                  .padStart(2, "0");
                                                const year = date.getFullYear();
                                                const hours = date
                                                  .getHours()
                                                  .toString()
                                                  .padStart(2, "0");
                                                return `${day}/${month}/${year} ${hours}:00`;
                                              }}
                                            />

                                            {currentDataKeys.map((dataKey, i) =>
                                              filteredData.some(
                                                (data) => data[dataKey] != null
                                              ) ? (
                                                <Area
                                                  key={dataKey}
                                                  type="monotone"
                                                  name={
                                                    dataKeyNames[dataKey] ||
                                                    dataKey
                                                  }
                                                  dataKey={dataKey}
                                                  stroke={currentColors[i]}
                                                  fillOpacity={0.3}
                                                  fill={`url(#color${chart.name})`}
                                                  yAxisId="left"
                                                />
                                              ) : null
                                            )}
                                          </AreaChart>
                                        </ResponsiveContainer>
                                      </div>

                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "center",
                                          gap: "20px",
                                          marginTop: "15px",
                                          flexWrap: "nowrap",
                                        }}
                                      >
                                        {currentDataKeys.map((dataKey, i) =>
                                          filteredData.some(
                                            (data) => data[dataKey] != null
                                          ) ? (
                                            <div
                                              key={dataKey}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                fontSize: "10px",
                                                color: "#4a5568",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  width: "12px",
                                                  height: "12px",
                                                  borderRadius: "50%",
                                                  backgroundColor:
                                                    currentColors[i],
                                                }}
                                              ></div>
                                              {dataKeyNames[dataKey] || dataKey}
                                            </div>
                                          ) : null
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </Box>
                          );
                        })}
                    </Stack>
                  )}
                </div>
              </div>
            )}
          </Main>
        </div>
      </Box>
    </div>
  );
}
